/**
 * サポート窓口（問い合わせ・障害情報・メンテナンス予定・権利行使請求）。
 *
 * この画面が無いあいだ、契約者から運営へ連絡する経路は「env に入っている
 * サポート URL（外部）」しか無く、SLA で約束した一次応答の期限も、
 * 自分に関係する障害情報も、契約者からは一切見えなかった。
 *
 * 守っている点。
 *
 * 1. **一次応答の期限を隠さない。** プランごとに約束している時間なので、
 *    こちらから見せる。見せないと「返事が来ない」の基準が契約者側に無い。
 * 2. **障害情報は公開文面だけ**（API が `public_message` のある行しか返さない）。
 *    内部の調査メモには他組織の名前やノード名が入りうる。
 * 3. **メールの本文に関する調査は依頼できない。** 本文を読む操作は運営でも
 *    時限付きの承認が要る統制対象なので、窓口の文言でもそれを前提にする。
 * 4. 権利行使請求（開示・訂正・削除）は法定の期限（受付から 14 日）で動く。
 *    問い合わせとは別の台帳になるので、画面でも別の入口にする。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CursorPagination,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  KeyValue,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Textarea,
  formatDateTime,
  useCursorPagination,
  type DataTableColumn,
} from '@mail-sv/ui';
import { ORG_PERMISSION } from '@mail-sv/shared';
import { CalendarClock, LifeBuoy, MessageSquarePlus, ShieldAlert, Siren } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { AppLink, useAppNavigate, useRouteParams } from '../lib/nav.js';
import { ErrorAlert } from '../components/async.js';
import { FormAlert, SubmitBar } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { hasPermission, useAuth } from '../stores/auth.js';
import type {
  CursorPage,
  CustomerIncident,
  MaintenanceWindow,
  PrivacyRequest,
  SupportTicketDetail,
  SupportTicketSummary,
} from '../lib/api-types.js';

const TICKET_KIND_LABEL: Record<string, string> = {
  support: '技術的なご相談',
  billing: 'ご請求について',
  security: 'セキュリティについて',
  privacy: '個人情報について',
};

const TICKET_STATUS_LABEL: Record<string, string> = {
  open: '受付済み',
  pending: '運営が対応中',
  awaiting_customer: 'お客様のご返信待ち',
  resolved: '解決済み',
  closed: '終了',
};

const PRIORITY_LABEL: Record<string, string> = {
  low: '低',
  normal: '通常',
  high: '高（業務が止まっている）',
};

const DSR_KIND_LABEL: Record<string, string> = {
  access: '開示の請求',
  rectification: '訂正の請求',
  erasure: '削除の請求',
  restriction: '利用停止の請求',
  portability: 'データポータビリティの請求',
  objection: '利用への異議',
};

export function SupportPage() {
  const { user } = useAuth();
  const canWrite = hasPermission(user, ORG_PERMISSION.supportWrite);
  const page = useCursorPagination(20);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [dsrOpen, setDsrOpen] = React.useState(false);

  const tickets = useQuery({
    queryKey: qk.supportTickets({ cursor: page.cursor, limit: page.limit }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      return api.get<CursorPage<SupportTicketSummary>>(`/support/tickets?${params.toString()}`);
    },
  });

  const incidents = useQuery({
    queryKey: qk.incidents({ open: true }),
    queryFn: () => api.get<CursorPage<CustomerIncident>>('/incidents?limit=20'),
  });

  const maintenance = useQuery({
    queryKey: qk.maintenanceWindows(),
    queryFn: () => api.get<{ items: MaintenanceWindow[] }>('/maintenance-windows'),
  });

  const privacyRequests = useQuery({
    queryKey: qk.privacyRequests(),
    queryFn: () => api.get<{ items: PrivacyRequest[] }>('/privacy-requests'),
    enabled: canWrite,
  });

  const openIncidents = (incidents.data?.items ?? []).filter((i) => i.resolvedAt === null);
  const upcoming = maintenance.data?.items ?? [];

  const columns: Array<DataTableColumn<SupportTicketSummary>> = [
    {
      id: 'subject',
      header: '件名',
      cell: (row) => (
        <AppLink to={`/support/tickets/${row.id}`} className="min-w-0 block">
          <p className="truncate text-sm font-medium text-primary">{row.subject}</p>
          <p className="truncate text-xs text-muted-foreground">
            {row.reference} / {TICKET_KIND_LABEL[row.kind] ?? row.kind}
          </p>
        </AppLink>
      ),
    },
    {
      id: 'status',
      header: '状態',
      width: 'w-40',
      cell: (row) => (
        <StatusBadge
          status={row.status}
          label={TICKET_STATUS_LABEL[row.status] ?? row.status}
          size="sm"
          dot
        />
      ),
    },
    {
      id: 'due',
      header: '一次応答の期限',
      width: 'w-44',
      hideBelow: 'md',
      cell: (row) => {
        if (row.firstRespondedAt) {
          return (
            <span className="tabular text-xs text-muted-foreground">
              回答済み {formatDateTime(row.firstRespondedAt)}
            </span>
          );
        }
        if (!row.firstResponseDueAt) return <span className="text-xs">—</span>;
        const overdue = new Date(row.firstResponseDueAt).getTime() < Date.now();
        return (
          <span className={`tabular text-xs ${overdue ? 'text-destructive' : ''}`}>
            {formatDateTime(row.firstResponseDueAt)}
          </span>
        );
      },
    },
    {
      id: 'lastMessageAt',
      header: '最終更新',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (row) => <span className="tabular text-xs">{formatDateTime(row.lastMessageAt)}</span>,
    },
  ];

  return (
    <>
      <PageHeader
        title="サポート"
        description="お問い合わせ、障害情報、メンテナンスの予定をこちらでご確認いただけます。"
        actions={
          canWrite ? (
            <Button onClick={() => setCreateOpen(true)}>
              <MessageSquarePlus aria-hidden />
              問い合わせる
            </Button>
          ) : null
        }
      />

      <div className="space-y-5">
        {/* 障害情報は最上段。問い合わせる前に「既知かどうか」が分かるようにする。 */}
        {openIncidents.length > 0 ? (
          <Section title="発生中の障害">
            <div className="space-y-3">
              {openIncidents.map((i) => (
                <Alert variant="destructive" key={i.id}>
                  <AlertTitle>
                    <span className="inline-flex items-center gap-2">
                      <Siren className="size-4" aria-hidden />
                      {i.title}
                    </span>
                  </AlertTitle>
                  <AlertDescription className="space-y-1">
                    <p className="whitespace-pre-wrap">{i.publicMessage}</p>
                    <p className="text-xs">
                      {i.reference} / 発生 {formatDateTime(i.startedAt)}
                    </p>
                  </AlertDescription>
                </Alert>
              ))}
            </div>
          </Section>
        ) : null}

        {upcoming.length > 0 ? (
          <Section title="メンテナンスの予定">
            <div className="space-y-3">
              {upcoming.map((m) => (
                <Card key={m.id}>
                  <CardContent className="space-y-1 p-4">
                    <p className="flex items-center gap-2 text-sm font-medium">
                      <CalendarClock className="size-4 text-muted-foreground" aria-hidden />
                      {m.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(m.startsAt)} 〜 {formatDateTime(m.endsAt)}
                    </p>
                    {m.bodyMd ? (
                      <p className="whitespace-pre-wrap text-sm text-muted-foreground">{m.bodyMd}</p>
                    ) : null}
                  </CardContent>
                </Card>
              ))}
            </div>
          </Section>
        ) : null}

        <Section title="お問い合わせ">
          {tickets.isError ? <ErrorAlert error={tickets.error} /> : null}
          <DataTable
            columns={columns}
            rows={tickets.data?.items ?? []}
            rowKey={(row) => row.id}
            loading={tickets.isPending}
            emptyTitle="お問い合わせはありません"
            emptyDescription="ご不明な点があれば「問い合わせる」からご連絡ください。"
            footer={
              <CursorPagination
                nextCursor={tickets.data?.nextCursor ?? null}
                canGoPrev={page.canGoPrev}
                onNext={() => page.goNext(tickets.data?.nextCursor ?? null)}
                onPrev={page.goPrev}
                limit={page.limit}
                onLimitChange={page.setLimit}
                currentCount={tickets.data?.items.length}
                loading={tickets.isFetching}
              />
            }
          />
          <HelpNote title="メールの本文についてのご依頼はお受けできません">
            運営スタッフがお客様のメールの<strong>本文を読むことはありません</strong>
            （そのための権限は通常付与されておらず、法令に基づく要請などの例外時のみ、
            記録が残る形で時限的に承認されます）。「届いたはずのメールを探してほしい」といった
            ご依頼は、配送の記録（送信元・宛先・日時・結果）の範囲でお調べします。
          </HelpNote>
        </Section>

        <Section
          title="個人情報に関するご請求"
          actions={
            canWrite ? (
              <Button variant="outline" size="sm" onClick={() => setDsrOpen(true)}>
                <ShieldAlert aria-hidden />
                請求する
              </Button>
            ) : null
          }
        >
          <p className="text-sm text-muted-foreground">
            ご本人またはご本人から委任を受けた方からの、開示・訂正・削除等のご請求を受け付けます。
            受付から <Badge variant="muted" size="sm">14 日</Badge> 以内に回答します。
          </p>
          {(privacyRequests.data?.items ?? []).length > 0 ? (
            <div className="space-y-2">
              {privacyRequests.data?.items.map((r) => (
                <Card key={r.id}>
                  <CardContent className="p-4">
                    <KeyValue
                      columns={3}
                      items={[
                        { key: '受付番号', value: r.reference },
                        { key: '種別', value: DSR_KIND_LABEL[r.kind] ?? r.kind },
                        {
                          key: '状態',
                          value: <StatusBadge status={r.status} size="sm" dot />,
                        },
                        { key: '受付', value: formatDateTime(r.receivedAt) },
                        { key: '回答期限', value: formatDateTime(r.dueAt) },
                        {
                          key: '完了',
                          value: r.completedAt ? formatDateTime(r.completedAt) : '—',
                        },
                      ]}
                    />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}
        </Section>
      </div>

      {createOpen ? <CreateTicketDialog onClose={() => setCreateOpen(false)} /> : null}
      {dsrOpen ? <CreatePrivacyRequestDialog onClose={() => setDsrOpen(false)} /> : null}
    </>
  );
}

/* ------------------------------------------------------------ 起票 */

function CreateTicketDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useAppNavigate();
  const [subject, setSubject] = React.useState('');
  const [bodyMd, setBodyMd] = React.useState('');
  const [kind, setKind] = React.useState('support');
  const [priority, setPriority] = React.useState('normal');

  const create = useMutation({
    mutationFn: () =>
      // 201 は `{id, reference, firstResponseDueAt, slaLabel}`。
      // **`ticketId` ではなく `id`**（推測で書くと必ず undefined になる）。
      api.post<{ id: string; reference: string; firstResponseDueAt: string; slaLabel: string }>(
        '/support/tickets',
        { subject, bodyMd, kind, priority },
      ),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['portal', 'support'] as const });
      onClose();
      if (res.id) navigate({ to: `/support/tickets/${res.id}` });
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>お問い合わせ</DialogTitle>
          <DialogDescription>
            できるだけ具体的にご記入ください（対象のアドレス・日時・表示されたメッセージなど）。
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <FormAlert error={create.error} />

          <Field label="種別">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TICKET_KIND_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            label="緊急度"
            hint="「高」は業務が止まっている場合にお選びください。一次応答の目標時間はご契約プランによって異なります。"
          >
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITY_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="件名">
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              required
            />
          </Field>

          <Field label="内容">
            <Textarea
              value={bodyMd}
              onChange={(e) => setBodyMd(e.target.value)}
              rows={8}
              maxLength={20_000}
              required
            />
          </Field>

          <SubmitBar
            submitLabel="送信する"
            pending={create.isPending}
            disabled={subject === '' || bodyMd === ''}
            onCancel={onClose}
            hint="メールのパスワードなど、秘密の情報は書かないでください。"
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CreatePrivacyRequestDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = React.useState('access');
  const [subjectEmail, setSubjectEmail] = React.useState('');
  const [subjectName, setSubjectName] = React.useState('');
  const [detail, setDetail] = React.useState('');

  const create = useMutation({
    mutationFn: () =>
      api.post('/privacy-requests', { kind, subjectEmail, subjectName, detail }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.privacyRequests() });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'support'] as const });
      onClose();
    },
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>個人情報に関するご請求</DialogTitle>
          <DialogDescription>
            受付から 14 日以内に回答します。ご本人確認のため、追加の情報をお伺いすることがあります。
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <FormAlert error={create.error} />

          <Field label="ご請求の種別">
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DSR_KIND_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="対象の方のメールアドレス">
            <Input
              type="email"
              value={subjectEmail}
              onChange={(e) => setSubjectEmail(e.target.value)}
              maxLength={254}
              required
            />
          </Field>

          <Field label="対象の方のお名前（任意）">
            <Input
              value={subjectName}
              onChange={(e) => setSubjectName(e.target.value)}
              maxLength={200}
            />
          </Field>

          <Field label="ご請求の内容">
            <Textarea
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              rows={6}
              maxLength={4000}
              required
            />
          </Field>

          <SubmitBar
            submitLabel="請求する"
            pending={create.isPending}
            disabled={subjectEmail === '' || detail === ''}
            onCancel={onClose}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------ 1 件の詳細 */

export function SupportTicketPage() {
  const params = useRouteParams();
  const ticketId = params.ticketId ?? '';
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canWrite = hasPermission(user, ORG_PERMISSION.supportWrite);
  const [reply, setReply] = React.useState('');

  const detail = useQuery({
    queryKey: qk.supportTicket(ticketId),
    queryFn: () =>
      api.get<SupportTicketDetail>(`/support/tickets/${encodeURIComponent(ticketId)}`),
    enabled: ticketId !== '',
  });

  const invalidate = React.useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: qk.supportTicket(ticketId) });
    void queryClient.invalidateQueries({ queryKey: ['portal', 'support', 'tickets'] as const });
  }, [queryClient, ticketId]);

  const post = useMutation({
    mutationFn: () =>
      api.post(`/support/tickets/${encodeURIComponent(ticketId)}/messages`, { bodyMd: reply }),
    onSuccess: () => {
      setReply('');
      invalidate();
    },
  });

  const close = useMutation({
    mutationFn: () => api.post(`/support/tickets/${encodeURIComponent(ticketId)}/close`),
    onSuccess: invalidate,
  });

  const ticket = detail.data;

  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'サポート', href: '/support' }, { label: '問い合わせ' }]}
        title={ticket?.subject ?? '問い合わせ'}
        description={ticket ? `${ticket.reference} / ${TICKET_KIND_LABEL[ticket.kind] ?? ticket.kind}` : undefined}
        actions={
          ticket && canWrite && ticket.closedAt === null ? (
            <Button variant="outline" size="sm" onClick={() => close.mutate()} loading={close.isPending}>
              解決したので閉じる
            </Button>
          ) : null
        }
      />

      {detail.isError ? <ErrorAlert error={detail.error} onRetry={() => void detail.refetch()} /> : null}

      {ticket ? (
        <div className="space-y-5">
          <Card>
            <CardContent className="p-5">
              <KeyValue
                columns={3}
                items={[
                  {
                    key: '状態',
                    value: (
                      <StatusBadge
                        status={ticket.status}
                        label={TICKET_STATUS_LABEL[ticket.status] ?? ticket.status}
                        size="sm"
                        dot
                      />
                    ),
                  },
                  { key: '緊急度', value: PRIORITY_LABEL[ticket.priority] ?? ticket.priority },
                  { key: '受付', value: formatDateTime(ticket.createdAt) },
                  {
                    key: '一次応答の期限',
                    value: ticket.firstRespondedAt
                      ? `回答済み（${formatDateTime(ticket.firstRespondedAt)}）`
                      : ticket.firstResponseDueAt
                        ? formatDateTime(ticket.firstResponseDueAt)
                        : '—',
                  },
                  {
                    key: '解決',
                    value: ticket.resolvedAt ? formatDateTime(ticket.resolvedAt) : '—',
                  },
                  { key: '終了', value: ticket.closedAt ? formatDateTime(ticket.closedAt) : '—' },
                ]}
              />
            </CardContent>
          </Card>

          <Section title="やり取り">
            {ticket.messages.length === 0 ? (
              <EmptyState icon={LifeBuoy} title="まだやり取りがありません" />
            ) : (
              <div className="space-y-3">
                {ticket.messages.map((m) => (
                  <Card key={m.id}>
                    <CardContent className="space-y-2 p-4">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge
                          variant={m.authorType === 'org_user' ? 'secondary' : 'info'}
                          size="sm"
                        >
                          {m.authorType === 'org_user' ? 'お客様' : '運営'}
                        </Badge>
                        <span className="text-xs font-medium">{m.authorLabel}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDateTime(m.createdAt)}
                        </span>
                      </div>
                      {/* 本文は素のテキストとして出す（HTML には解釈しない）。 */}
                      <p className="whitespace-pre-wrap text-sm">{m.bodyMd}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </Section>

          {canWrite && ticket.closedAt === null ? (
            <Section title="返信する">
              <Card>
                <CardContent className="space-y-3 p-5">
                  <FormAlert error={post.error ?? close.error} />
                  <Textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={5}
                    maxLength={20_000}
                    placeholder="ご返信を入力してください"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={() => post.mutate()}
                      loading={post.isPending}
                      disabled={reply.trim() === ''}
                    >
                      送信する
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </Section>
          ) : ticket.closedAt !== null ? (
            <Alert variant="info">
              <AlertTitle>この問い合わせは終了しています</AlertTitle>
              <AlertDescription>
                続きのご相談は、サポート画面から新しくお問い合わせください。
              </AlertDescription>
            </Alert>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
